<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import LandingFooter from "@/components/landing/LandingFooter.vue";
import LandingHeader from "@/components/landing/LandingHeader.vue";
import { UserStore } from "@/store/userStore";
import { RouterLink } from "vue-router";
import HeroBgLight from "@/assets/images/HeroBgLight.png";
import HeroBgDark from "@/assets/images/HeroBgDark.png";

const userStore = UserStore();
const mobileMenuOpen = ref(false);
const landingPageRef = ref<HTMLElement | null>(null);
let revealObserver: IntersectionObserver | null = null;

type LandingCard = {
  heading: string;
  text: string;
  image?: string;
  icon?: string;
};

type FAQ = {
  heading: string;
  text: string;
};

const cards: LandingCard[] = [
  {
    heading: "Clear visibility across projects",
    text: "Understand where your team spends time and how projects progress.",
    icon: "M14.825 9.825Q16 8.65 16 7V4H8v3q0 1.65 1.175 2.825T12 11t2.825-1.175M5 22q-.425 0-.712-.288T4 21t.288-.712T5 20h1v-3q0-1.525.713-2.863T8.7 12q-1.275-.8-1.987-2.137T6 7V4H5q-.425 0-.712-.288T4 3t.288-.712T5 2h14q.425 0 .713.288T20 3t-.288.713T19 4h-1v3q0 1.525-.712 2.863T15.3 12q1.275.8 1.988 2.138T18 17v3h1q.425 0 .713.288T20 21t-.288.713T19 22z",
  },
  {
    heading: "Easier time tracking",
    text: "Understand where your team spends time and how projects progress.Easier time tracking",
    icon: "M14.363 4.638Q14 4.275 14 3.75t.363-.888t.887-.362t.888.363t.362.887t-.363.888T15.25 5t-.888-.363m0 16.5Q14 20.776 14 20.25t.363-.888t.887-.362t.888.363t.362.887t-.363.888t-.887.362t-.888-.363m4-13Q18 7.775 18 7.25t.363-.888T19.25 6t.888.363t.362.887t-.363.888t-.887.362t-.888-.363m0 9.5Q18 17.276 18 16.75t.363-.888t.887-.362t.888.363t.362.887t-.363.888t-.887.362t-.888-.363m1.5-4.75Q19.5 12.526 19.5 12t.363-.888t.887-.362t.888.363T22 12t-.363.888t-.887.362t-.888-.363M4.613 5.25q2.613-2.825 6.413-3.2q.4-.05.688.238T12 3q0 .4-.262.7t-.663.35q-3.025.35-5.05 2.6T4 12q0 3.125 2.025 5.363t5.05 2.587q.4.05.663.35T12 21q0 .425-.288.713t-.687.237Q7.2 21.575 4.6 18.75T2 12t2.613-6.75m5.975 8.163Q10 12.825 10 12q0-.125.013-.262t.062-.263L8.7 10.1q-.275-.275-.275-.7t.275-.7t.7-.275t.7.275l1.375 1.375q.1-.025.525-.075q.825 0 1.413.588T14 12t-.587 1.413T12 14t-1.412-.587",
  },
  {
    heading: "Better project organization",
    text: "Understand where your team spends time and how projects progress.",
    icon: "M8 16.1q-.825 0-1.412-.587T6 14.1V5q0-.825.588-1.412T8 3h12q.825 0 1.413.588T22 5v9.1q0 .825-.587 1.413T20 16.1zm0-2h12V5H8zm0 0V5zM17 9q.425 0 .713-.288T18 8t-.288-.712T17 7h-6.025q-.425 0-.7.288T10 8t.288.713T11 9zm-3 3q.425 0 .713-.288T15 11t-.288-.712T14 10h-3.025q-.425 0-.7.288T10 11t.288.713T11 12zm-8.55 8.975q-.825.125-1.475-.375T3.2 19.275l-1.325-9.9q-.05-.425.2-.75t.675-.375q.4-.05.725.188t.375.662l1.4 9.9l6.85-.95l4.1-.575q.45-.075.8.225t.35.775q0 .375-.25.663t-.625.337z",
  },
  {
    heading: "Reliable reporting",
    text: "Understand where your team spends time and how projects progress.",
    icon: "M17 20q-.425 0-.712-.288T16 19v-5q0-.425.288-.712T17 13h2q.425 0 .713.288T20 14v5q0 .425-.288.713T19 20zm-6 0q-.425 0-.712-.288T10 19V5q0-.425.288-.712T11 4h2q.425 0 .713.288T14 5v14q0 .425-.288.713T13 20zm-6 0q-.425 0-.712-.288T4 19v-9q0-.425.288-.712T5 9h2q.425 0 .713.288T8 10v9q0 .425-.288.713T7 20z",
  },
];

const imgCards: LandingCard[] = [
  {
    heading: "Project tracking",
    text: "Monitor project progress, priorities, and deadlines from a central dashboard.",
    image: "/src/assets/images/ProjectTracking.png",
  },
  {
    heading: "Integrated timesheets",
    text: "Log hours directly while working on tasks without switching tools.",
    image: "/src/assets/images/IntegratedTimesheets.png",
  },
  {
    heading: "CSV export",
    text: "Export timesheet data for reporting, analysis, or accounting.",
    image: "/src/assets/images/CSVExport.png",
  },
  {
    heading: "Task hierarchy",
    text: "Break work into tasks and subtasks so everyone understands what needs to be done.",
    image: "/src/assets/images/TaskHierarchy.png",
  },
];

const blankCards: LandingCard[] = [
  {
    heading: "Will my team actually use it?",
    text: "OmniTakser is designed to make time logging simple and part of everyday work.",
  },
  {
    heading: "Is it difficult to set up?",
    text: "Companies can be created quickly, and projects and teams can be added in minutes.",
  },
  {
    heading: "Can we access our data?",
    text: "All timesheet data can be exported as CSV for further reporting or analysis.",
  },
];

const faqs: FAQ[] = [
  {
    heading: "How is OmniTakser different from other time tracking tools?",
    text: "OmniTakser combines task management and timesheets in one system. Instead of switching between tools, teams can manage work and track time in the same place.",
  },
  {
    heading: "Do employees have to log hours manually?",
    text: "Yes, but the process is simple and integrated into tasks. This reduces friction and helps ensure more accurate and consistent time tracking.",
  },
  {
    heading: "Can we track billable and non-billable hours?",
    text: "Yes. OmniTakser allows you to categorize hours, so you can clearly understand how time is distributed across projects and activities.",
  },
  {
    heading: "Can managers edit or review time entries?",
    text: "Yes. Managers can view, edit, and organize time logs across the entire team, ensuring accuracy and consistency.",
  },
  {
    heading: "How do we get reports from the system?",
    text: "All time data can be exported as CSV. This makes it easy to analyze hours, prepare reports, or share data with accounting tools.",
  },
  {
    heading: "Is OmniTakser suitable for small teams?",
    text: "Yes. OmniTakser is designed for teams of 5–50 people who need better structure without adding complexity.",
  },
  {
    heading: "How long does it take to set up?",
    text: "Setup is quick. You can create your company, add projects, and assign team members in just a few steps.",
  },
  {
    heading: "Can we organize work into tasks and subtasks?",
    text: "Yes. Projects can be broken down into tasks and subtasks, making it easier to manage work and track progress.",
  },
  {
    heading: "Will OmniTakser replace our spreadsheets?",
    text: "Yes. OmniTakser is built to replace spreadsheets and scattered tools with a single, structured system.",
  },
  {
    heading: "Can we track project progress as well as time?",
    text: "Yes. OmniTakser combines project tracking with time tracking, so you can see both progress and effort in one place.",
  },
];

const openFaqIndexes = ref<number[]>([0]);

const toggleFaq = (index: number) => {
  openFaqIndexes.value = openFaqIndexes.value.includes(index)
    ? openFaqIndexes.value.filter((faqIndex) => faqIndex !== index)
    : [...openFaqIndexes.value, index];
};

const isFaqOpen = (index: number) => openFaqIndexes.value.includes(index);

const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
};

const handleResize = () => {
  if (window.innerWidth > 650) {
    closeMobileMenu();
  }
};

const initializeScrollReveal = async () => {
  await nextTick();

  revealObserver?.disconnect();

  if (!landingPageRef.value) return;

  const revealElements =
    landingPageRef.value.querySelectorAll<HTMLElement>(".scroll-reveal");

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add("is-visible");
        revealObserver?.unobserve(entry.target);
      });
    },
    {
      threshold: 0.2,
      rootMargin: "0px 0px -10% 0px",
    }
  );

  revealElements.forEach((element) => {
    revealObserver?.observe(element);
  });
};

watch(
  () => userStore.theme,
  (newVal) => {
    // eslint-disable-next-line no-storage/no-browser-storage
    localStorage.setItem("theme", newVal ? "light" : "dark");
    userStore.setTheme();
  }
);

onMounted(() => {
  // eslint-disable-next-line no-storage/no-browser-storage
  userStore.theme = localStorage.getItem("theme") === "light";
  userStore.setTheme();

  void initializeScrollReveal();
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);

  revealObserver?.disconnect();
});
</script>
<template>
  <div ref="landingPageRef">
    <div class="bg">
      <img
        :src="userStore.theme ? HeroBgLight : HeroBgDark"
        alt="bg"
        class="hero-bg"
      />
      <div class="content">
        <LandingHeader></LandingHeader>

        <section class="hero-container">
          <div class="hero-text">
            <h1>
              Track work, tasks, and billable hours
              <span class="italic">in one place</span>
            </h1>
            <p>
              OmniTakser helps teams organize projects, manage tasks, and log
              hours effortlessly — giving managers clear insight into how time
              is spent across the company.
            </p>
            <RouterLink
to="/contact"
class="primary-filled-button"
              >Contact us</RouterLink
            >
          </div>
          <img
            class="hero-product-image"
            src="/src/assets/images/hero-image.png"
            alt="product-screenshot"
          />
        </section>
        <section class="scroll-reveal">
          <h2>
            <span class="italic">Work shouldn't be scattered </span>across
            spreadsheets and tools
          </h2>
          <div class="content-wrapper">
            <div class="pills-container">
              <div class="pill-wrapper left">
                <div class="pill">
                  <p>Time tracking in spreadsheets</p>
                </div>
              </div>
              <div class="pill-wrapper right">
                <div class="pill">
                  <p>Tasks managed in separate tools</p>
                </div>
              </div>
              <div class="pill-wrapper left">
                <div class="pill">
                  <p>Manual reporting for project hours</p>
                </div>
              </div>
              <div class="pill-wrapper right">
                <div class="pill">
                  <p>Limited visibility for managers</p>
                </div>
              </div>
            </div>
            <img src="/src/assets/images/arrow-down.svg" alt="" />
            <div class="text-card">
              <h3>
                OmniTakser combines tasks, projects, and timesheets in one
                system
              </h3>
              <p>
                Managers gain insight into team work and billable hours, while
                employees track tasks and time in the same place.
              </p>
            </div>
          </div>
        </section>
        <section class="scroll-reveal">
          <h2>
            Everything you need to
            <span class="italic">manage work and time</span>
          </h2>
          <div class="cards-container">
            <div v-for="card in cards" :key="card.heading" class="card">
              <svg
                v-if="card.icon"
                xmlns="http://www.w3.org/2000/svg"
                width="{24}"
                height="{24}"
                viewBox="0 0 24 24"
                class="card-icon"
              >
                <path fill="#2c75f4" :d="card.icon"></path>
              </svg>
              <img
                v-if="card.image"
                class="card-image"
                :src="card.image"
                :alt="card.heading"
              />
              <div class="card-text">
                <h3>{{ card.heading }}</h3>
                <p>{{ card.text }}</p>
              </div>
            </div>
          </div>
        </section>
        <section class="scroll-reveal">
          <h2>
            Designed for how teams
            <span class="italic">actually work</span>
          </h2>
          <div class="image-cards-container">
            <div
              v-for="imgCard in imgCards"
              :key="imgCard.heading"
              class="card"
            >
              <div class="img-container">
                <img
                  v-if="imgCard.image"
                  class="card-image"
                  :src="imgCard.image"
                  :alt="imgCard.heading"
                />
              </div>
              <div class="card-text">
                <h3>{{ imgCard.heading }}</h3>
                <p>{{ imgCard.text }}</p>
              </div>
            </div>
          </div>
        </section>
        <section class="scroll-reveal">
          <h2>Frequently asked questions</h2>
          <div class="blank-cards-container">
            <div
              v-for="blankCard in blankCards"
              :key="blankCard.heading"
              class="card"
            >
              <div class="card-text">
                <h3>{{ blankCard.heading }}</h3>
                <p>{{ blankCard.text }}</p>
              </div>
            </div>
          </div>
        </section>
        <section class="scroll-reveal">
          <h2>More FAQ’s</h2>
          <div class="faqs-container">
            <article
              v-for="(faq, index) in faqs"
              :key="faq.heading"
              class="faq"
              :class="{ open: isFaqOpen(index) }"
            >
              <button
                type="button"
                class="faq-trigger"
                :aria-expanded="isFaqOpen(index)"
                @click="toggleFaq(index)"
              >
                <h3>{{ faq.heading }}</h3>
                <span class="faq-icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 6L9 12L15 18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </button>
              <p v-show="isFaqOpen(index)" class="faq-answer">{{ faq.text }}</p>
            </article>
          </div>
        </section>
        <div
          class="cta-container scroll-reveal"
          :style="{
            backgroundImage: userStore.theme
              ? 'url(/src/assets/images/CtaBgLight.png)'
              : 'url(/src/assets/images/CtaBgDark.png)',
          }"
        >
          <div class="cta-items">
            <h2>
              <span class="italic">Bring clarity</span> to your team's work and
              time tracking
            </h2>
            <p>
              See how OmniTakser can help your team organize projects and
              understand how time is spent across the company.
            </p>
            <RouterLink
to="/contact"
class="primary-filled-button"
              >Contact us</RouterLink
            >
          </div>
        </div>
        <LandingFooter></LandingFooter>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.bg {
  // position: relative;
  background-color: var(--figma-color-background-bg-dark);
  font-family: "Archivo", Arial, Helvetica, sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.italic {
  font-style: italic;
  font-weight: 400;
}

// * {
//   outline: 1px solid red;
// }

.content {
  z-index: 2;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hero-bg {
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 0;
  width: 100%;
  height: auto;
  object-fit: cover;
  object-position: top center;
  transform: translateX(-50%);
  pointer-events: none;
}

a {
  display: inline-flex;
  padding: 0.7rem 1.25rem;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 30rem;
  font-size: var(--figma-font-size-xl, 1.5rem);
  font-style: normal;
  font-weight: var(--figma-font-weight-bold, 700);
  text-decoration: none;
}

button {
  display: inline-flex;
  padding: 0.7rem 1.25rem;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 30rem;
  font-size: var(--figma-font-size-xl, 1.5rem);
  font-style: normal;
  font-weight: var(--figma-font-weight-bold, 700);
}

section {
  padding-block: var(--figma-space-7xl);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--figma-space-4xl);
  max-width: 1262px;
}

.primary-filled-button {
  border: 2px solid var(--figma-color-button-primary-filled-stroke, #163f97);
  background: var(--figma-color-button-primary-filled-background, #1a52c2);

  /* button/primary-raised */
  box-shadow: 0 -2px 4px 0 var(
        --figma-color-button-primary-filled-shadow,
        #163f97
      ) inset,
    0 4px 4px 0 var(--figma-color-button-primary-filled-highlight, #1f63e0)
      inset,
    0 4px 8px -2px var(--figma-color-shadow-md-1, rgba(10, 13, 18, 0.1)),
    0 2px 4px -2px var(--figma-color-shadow-md-2, rgba(10, 13, 18, 0.06));

  color: var(--figma-color-button-primary-filled-text, #fcfdfe);
}

.primary-outlined-button {
  border: 2px solid var(--figma-color-button-primary-outlined-stroke, #163f97);
  background: var(--figma-color-button-primary-outlined-fill, #1a52c2);
  color: var(--figma-color-button-primary-outlined-text, #0a1d47);
}

h1 {
  font-size: var(--figma-typography-font-size-text-6xl);
  line-height: var(--figma-line-height-tight);
  color: var(--figma-color-text-text);
  text-align: center;

  span {
    font-weight: 400;
    color: var(--figma-color-text-logo-blue);
  }
}

p {
  font-size: var(--figma-typography-font-size-text-xl);
  text-align: center;
  color: var(--figma-color-text-text-muted);
}

.hero-container {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--figma-space-4xl);
  padding-block: var(--figma-space-5xl);
}

.hero-product-image,
.hero-text {
  width: 100%;
  height: auto;
}

.hero-product-image {
  border-radius: var(--border-radius-2xl, 2rem);
  box-shadow: 0 0 27.8px 0 rgba(44, 117, 244, 0.13),
    0 0 55.8px 12px rgba(176, 201, 246, 0.14);
}

.hero-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: 400;
  gap: var(--figma-space-xl);

  a {
    background: linear-gradient(
      182deg,
      var(--figma-color-button-primary-filled-background, #1a52c2) -2.95%,
      var(--figma-color-brand-primary-full-opacity-400, #6fa0ff) 101.35%
    );
    box-shadow: 0 -2px 4px 0 var(
          --figma-color-button-primary-filled-shadow,
          #163f97
        ) inset,
      0 4px 4px 0 var(--figma-color-button-primary-filled-highlight, #1f63e0)
        inset,
      0 4px 8px -2px var(--figma-color-shadow-md-1, rgba(10, 13, 18, 0.18)),
      0 2px 4px -2px var(--figma-color-shadow-md-2, rgba(10, 13, 18, 0.16));
  }

  p {
    max-width: 890px;
  }
}

h2 {
  font-size: var(--figma-typography-font-size-text-4xl);
  max-width: 760px;
  line-height: var(--figma-line-height-snug);
  text-align: center;
  font-weight: 600;

  span {
    font-weight: 600;
  }
}

.pill-wrapper {
  display: inline-block;
  padding: 1px;
  border-radius: 12px;
  background: linear-gradient(
    182deg,
    var(--figma-color-gray-cool-500, #6fa0ff),
    var(--figma-color-gray-cool-700, #2d2f39)
  );
}

.pill {
  display: flex;
  padding: 0.625rem;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: inherit;
  background-color: var(--figma-color-background-bg-dark);

  p {
    color: var(--figma-color-text-text-muted);
    font-size: var(--figma-typography-font-size-text-md);
  }
}

.left {
  transform: rotate(-3.338deg);
}

.right {
  transform: rotate(2.534deg);
}

.pills-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

h3 {
  font-size: var(--figma-typography-font-size-text-xl);
  color: var(--figma-color-text-text);
}

.text-card {
  display: flex;
  max-width: 60rem;
  padding: 2rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  border-radius: var(--border-radius-xl, 1.5rem);
  border: 1px solid var(--figma-color-border-card, #3f404b);
  background: var(--figma-color-background-bg, #121521);

  box-shadow: 0 0 15px 0 rgba(44, 117, 244, 0.13),
    0 0 30px 8px rgba(176, 201, 246, 0.14);

  p {
    color: var(--figma-color-text-text-muted);
    font-size: var(--figma-typography-font-size-text-lg);
    text-align: left;
  }
}

.content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--figma-space-xl);

  img {
    max-height: 4.2rem;
  }
}

.card-icon {
  height: 2.5rem;
  width: 2.5rem;
}

.card-icon {
  height: 2.5rem;
  width: 2.5rem;
}

.card {
  display: flex;
  padding: 1.5em;
  width: 100%;
  min-width: 0;
  padding: 1.5rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  border-radius: var(--border-radius-xl, 1.5rem);
  border: 1px solid var(--figma-color-border-card, #3f404b);
  background: var(--figma-color-background-bg, #121521);

  img {
    width: 100%;
    border-radius: 1rem;
    height: 376px;
    object-fit: cover;
  }

  p {
    text-align: left;
    font-size: var(--figma-typography-font-size-text-lg);
  }
}

.cards-container {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--figma-space-sm);
}

.image-cards-container {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: var(--figma-space-sm);

  .card {
    width: auto;
    min-width: 0;
    height: 100%;
  }

  .card:nth-child(1) {
    grid-column: 1 / span 3;
  }

  .card:nth-child(2) {
    grid-column: 4 / span 2;
  }

  .card:nth-child(3) {
    grid-column: 1 / span 2;
  }

  .card:nth-child(4) {
    grid-column: 3 / span 3;
  }
}

.blank-cards-container {
  display: flex;
  width: 100%;
  gap: var(--figma-space-xl);

  h3 {
    color: var(--figma-color-button-primary-filled-background);
  }
}

.faqs-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: var(--figma-space-lg);
}

.faq {
  width: 100%;
  padding: var(--figma-space-xl);
  border-radius: var(--border-radius-xl, 1.5rem);
  background: var(--figma-color-background-bg, #121521);
}

.faq-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--figma-space-md);
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  box-shadow: none;
  border-radius: 0;
  text-align: left;

  h3 {
    font-family: var(--figma-typography-font-family-accent);
    font-size: var(--figma-typography-font-size-text-xl);
    font-weight: var(--figma-typography-font-weight-semi-bold);
    line-height: var(--figma-line-height-normal);
    color: var(--figma-color-text-text);
  }
}

.faq-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 1.5rem;
  width: 1.5rem;
  height: 1.5rem;
  color: var(--figma-color-text-text);
  transform: rotate(-90deg);
  transition: transform 180ms ease;
  cursor: pointer;

  svg {
    width: 100%;
    height: 100%;
  }
}

.faq.open .faq-icon {
  transform: rotate(90deg);
}

.faq-answer {
  margin-top: var(--figma-space-md);
  font-size: var(--figma-typography-font-size-text-lg);
  line-height: var(--figma-line-height-normal);
  color: var(--figma-color-text-text-muted);
  text-align: left;
}

.fade-down-enter-active,
.fade-down-leave-active {
  transition: opacity 160ms ease, transform 160ms ease;
}

.fade-down-enter-from,
.fade-down-leave-to {
  opacity: 0;
  transform: translateY(-0.375rem);
}

.scroll-reveal {
  opacity: 0;
  transform: translateY(-3rem);
  transition: opacity 700ms ease, transform 700ms ease;
  will-change: opacity, transform;
}

.scroll-reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.cta-container {
  width: 100%;
  padding-top: 30rem;
  padding-bottom: 20rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.cta-items {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--figma-space-2xl);
  max-width: 1262px;

  h2 {
    max-width: 100%;
  }

  p {
    font-size: var(--figma-font-size-lg);
  }

  span {
    color: var(--figma-color-text-logo-blue);
  }
}

@media (max-width: 1400px) {
  section {
    padding-block: var(--figma-space-5xl);
    padding-inline: var(--figma-space-3xl);
  }

  h1 {
    font-size: var(--figma-typography-font-size-text-4xl);
  }

  p {
    font-size: var(--figma-typography-font-size-text-lg);
  }

  h2 {
    font-size: var(--figma-typography-font-size-text-3xl);
  }

  h3 {
    font-size: var(--figma-typography-font-size-text-xl);
  }
}

@media (max-width: 1100px) {
  .image-cards-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    .card:nth-child(1) {
      grid-column: 1 / span 1;
    }
    .card:nth-child(2) {
      grid-column: 2 / span 1;
    }
    .card:nth-child(3) {
      grid-column: 1 / span 1;
    }
    .card:nth-child(4) {
      grid-column: 2 / span 1;
    }
  }

  .card {
    img {
      width: 100%;
      height: auto;
      aspect-ratio: 16 / 9;
      object-fit: cover;
    }
  }

  .cta-container {
    width: 100%;
    padding-top: 10rem;
    padding-bottom: 10rem;
    padding-inline: var(--figma-space-3xl);
  }
}

@media (max-width: 800px) {
  .pills-container {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    .card:nth-child(1) {
      grid-column: 1 / span 1;
    }
    .card:nth-child(2) {
      grid-column: 2 / span 1;
    }
    .card:nth-child(3) {
      grid-column: 1 / span 1;
    }
    .card:nth-child(4) {
      grid-column: 2 / span 1;
    }

    .pill-wrapper:nth-child(1) {
      transform: rotate(-1.38deg);
    }
    .pill-wrapper:nth-child(2) {
      transform: rotate(-1.534deg);
    }
    .pill-wrapper:nth-child(3) {
      transform: rotate(1.338deg);
    }
    .pill-wrapper:nth-child(4) {
      transform: rotate(1.534deg);
    }
  }

  .cards-container {
    display: flex;
    flex-direction: column;
    gap: var(--figma-space-lg);
  }

  .image-cards-container {
    display: flex;
    flex-direction: column;
    gap: var(--figma-space-lg);
  }

  .blank-cards-container {
    display: flex;
    flex-direction: column;
    gap: var(--figma-space-lg);
  }
}

@media (max-width: 700px) {
  section {
    padding-block: var(--figma-space-4xl);
    padding-inline: var(--figma-space-3xl);
    gap: var(--figma-space-3xl);

    h1 {
      font-size: var(--figma-typography-font-size-text-3xl);
    }

    p {
      font-size: var(--figma-typography-font-size-text-base);
    }

    h2 {
      font-size: var(--figma-typography-font-size-text-2xl);
    }

    h3 {
      font-size: var(--figma-typography-font-size-text-lg);
    }

    footer {
      width: 100%;
      padding-block: var(--figma-space-2xl);
      padding-inline: var(--figma-space-2xl);
    }

    .hero-product-image {
      border-radius: 1rem;
    }
  }

  button,
  a {
    font-size: var(--figma-typography-font-size-text-lg);
    padding: 0.5rem 1rem;
  }
}

@media (max-width: 600px) {
  footer {
    gap: var(--figma-space-lg);
  }

  .footer-logo-mobile {
    display: block;
    height: 0.625rem;
    width: auto;
  }

  .footer-logo-desktop {
    display: none;
  }
}

@media (max-width: 500px) {
  h1 {
    font-size: var(--figma-typography-font-size-text-2xl);
  }

  p {
    font-size: var(--figma-typography-font-size-text-base);
  }

  h2 {
    font-size: var(--figma-typography-font-size-text-xl);
  }

  h3 {
    font-size: var(--figma-typography-font-size-text-lg);
  }

  .pills-container {
    display: flex;
    flex-direction: column;
    gap: var(--figma-space-md);
  }
}
</style>
